import {
  UserStats,
  LevelProgress,
  ChordStats,
  ScaleStats,
  IntervalStats,
  KeyStats,
  DailyStats,
  GameModeStats,
  Achievement,
  ACHIEVEMENTS,
} from '../types/stats';
import { InstrumentType } from '../types/instruments';
import { APP_VERSION } from '../version';

const STORAGE_KEYS = {
  USER_STATS: 'keyperfect_user_stats',
  LEVEL_PROGRESS: 'keyperfect_level_progress',
  CHORD_STATS: 'keyperfect_chord_stats',
  SCALE_STATS: 'keyperfect_scale_stats',
  INTERVAL_STATS: 'keyperfect_interval_stats',
  KEY_STATS: 'keyperfect_key_stats',
  NOTE_STATS: 'keyperfect_note_stats',
  MUSIC_KEYS_PROGRESS: 'keyperfect_music_keys_progress',
  NOTES_PROGRESS: 'keyperfect_notes_progress',
  DAILY_STATS: 'keyperfect_daily_stats',
  GAME_MODE_STATS: 'keyperfect_game_mode_stats',
  ACHIEVEMENTS: 'keyperfect_achievements',
  SETTINGS: 'keyperfect_settings',
  SESSION_HISTORY: 'keyperfect_session_history',
  USED_INSTRUMENTS: 'keyperfect_used_instruments',
  WEEKLY_GOALS: 'keyperfect_weekly_goals',
  STREAK_FREEZE: 'keyperfect_streak_freeze',
  SOCIAL_CHALLENGES: 'keyperfect_social_challenges',
  MASTERY_DATA: 'keyperfect_mastery_data',
} as const;

/**
 * A date as YYYY-MM-DD in the *user's* timezone.
 *
 * These keys drive the daily streak, the per-item "last attempted" stamps and
 * the practice-history buckets. They used to come from `toISOString()`, which
 * is UTC, so west of UTC the day rolled over in the afternoon: at UTC-7 a
 * session at 6pm Tuesday was filed as Wednesday. That broke the streak in
 * both directions — playing Monday morning then Tuesday evening looked like a
 * skipped day and reset the streak, while genuinely skipping Tuesday could
 * look consecutive.
 */
export function localDateKey(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * The key for `daysAgo` days before `from`, in local time.
 *
 * Built from local date components rather than by subtracting milliseconds,
 * so it stays correct across a daylight-saving transition where a "day" is
 * 23 or 25 hours long.
 */
export function localDateKeyDaysAgo(daysAgo: number, from: Date = new Date()): string {
  return localDateKey(new Date(from.getFullYear(), from.getMonth(), from.getDate() - daysAgo));
}

// Default values
function getDefaultUserStats(): UserStats {
  const today = localDateKey();
  return {
    totalXP: 0,
    currentLevel: 1,
    totalQuestionsAnswered: 0,
    totalCorrect: 0,
    totalIncorrect: 0,
    currentStreak: 0,
    longestStreak: 0,
    totalPlayTime: 0,
    sessionsPlayed: 0,
    lastPlayedDate: today,
    joinedDate: today,
  };
}

function getDefaultDailyStats(): DailyStats {
  return {
    lastPlayedDate: '',
    currentStreak: 0,
    longestStreak: 0,
    totalDaysPlayed: 0,
    completed: false,
    todayScore: 0,
    todayQuestions: 0,
  };
}

export interface AppSettings {
  instrument: InstrumentType;
  volume: number;
  soundEffects: boolean;
  autoAdvance: boolean;
  showHints: boolean;
  playMode: 'chord' | 'arpeggio';
  /**
   * `system` follows the OS appearance (prefers-color-scheme) and re-resolves
   * when it changes; the others are fixed. See utils/theme.ts.
   */
  theme: 'system' | 'dark' | 'light' | 'purple' | 'blue';
  /**
   * How see-through glass surfaces are. Mirrors the iOS 27 Liquid Glass
   * slider: `clear` is the most translucent, `tinted` the most opaque and
   * readable. Missing on settings saved before this existed; treat as
   * `balanced`.
   */
  glassIntensity?: 'clear' | 'balanced' | 'tinted';
  notifications: boolean;
  /**
   * Whether the player has opted into Web MIDI. Off until they ask for it:
   * requesting MIDI access pops a browser permission prompt, and firing that
   * at someone who is halfway through a chord round only teaches them to
   * press Block. See useMIDIInput's autoConnect option.
   */
  midiEnabled: boolean;
  /** Whether Home's "All modes" list is left open. Collapsed for new players. */
  homeModesExpanded?: boolean;
  lastPreset?: PracticePresetId;
}

// Practice Presets
export type PracticePresetId = 'quick' | 'standard' | 'deep' | 'random';

export interface PracticePreset {
  id: PracticePresetId;
  name: string;
  description: string;
  icon: string;
  questionCount: number;
  difficulty: 'easy' | 'medium' | 'hard' | 'progressive';
  timeLimit?: number; // in seconds, undefined = no limit
  modes: ('chords' | 'scales' | 'intervals')[];
}

export const PRACTICE_PRESETS: Record<PracticePresetId, PracticePreset> = {
  quick: {
    id: 'quick',
    name: 'Quick Practice',
    description: '5 questions, easy difficulty',
    icon: '⚡',
    questionCount: 5,
    difficulty: 'easy',
    timeLimit: 120,
    modes: ['chords'],
  },
  standard: {
    id: 'standard',
    name: 'Standard Session',
    description: '10 questions, progressive difficulty',
    icon: '📚',
    questionCount: 10,
    difficulty: 'progressive',
    modes: ['chords', 'scales'],
  },
  deep: {
    id: 'deep',
    name: 'Deep Focus',
    description: '25 questions, harder content',
    icon: '🧠',
    questionCount: 25,
    difficulty: 'hard',
    modes: ['chords', 'scales', 'intervals'],
  },
  random: {
    id: 'random',
    name: 'Random Mix',
    description: 'All modes shuffled together',
    icon: '🎲',
    questionCount: 15,
    difficulty: 'progressive',
    modes: ['chords', 'scales', 'intervals'],
  },
};

// Practice Session History
export interface PracticeSession {
  id: string;
  date: string;
  mode: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  accuracy: number;
  duration: number; // in seconds
  xpEarned: number;
  streak: number;
}

function getDefaultSettings(): AppSettings {
  return {
    instrument: 'piano',
    volume: 0.7,
    soundEffects: true,
    autoAdvance: true,
    showHints: true,
    playMode: 'chord',
    theme: 'dark',
    glassIntensity: 'balanced',
    notifications: true,
    midiEnabled: false,
  };
}

// Generic storage helpers
function getItem<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function setItem<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
}

// User Stats
export function getUserStats(): UserStats {
  return getItem(STORAGE_KEYS.USER_STATS, getDefaultUserStats());
}

export function updateUserStats(updates: Partial<UserStats>): UserStats {
  const current = getUserStats();
  const updated = { ...current, ...updates };
  setItem(STORAGE_KEYS.USER_STATS, updated);
  return updated;
}

export function addXP(amount: number): UserStats {
  const stats = getUserStats();
  return updateUserStats({
    totalXP: stats.totalXP + amount,
  });
}

// Level Progress
export function getLevelProgress(): LevelProgress[] {
  return getItem(STORAGE_KEYS.LEVEL_PROGRESS, []);
}

export function updateLevelProgress(levelId: number, updates: Partial<LevelProgress>): LevelProgress[] {
  const progress = getLevelProgress();
  const index = progress.findIndex(p => p.levelId === levelId);

  if (index >= 0) {
    progress[index] = { ...progress[index], ...updates };
  } else {
    progress.push({
      levelId,
      questionsCompleted: 0,
      questionsRequired: 20,
      bestScore: 0,
      timesCompleted: 0,
      lastPlayedDate: localDateKey(),
      ...updates,
    });
  }

  setItem(STORAGE_KEYS.LEVEL_PROGRESS, progress);
  return progress;
}

// Chord Stats
export function getChordStats(): ChordStats[] {
  return getItem(STORAGE_KEYS.CHORD_STATS, []);
}

export function updateChordStats(chordType: string, correct: boolean): ChordStats[] {
  const stats = getChordStats();
  const index = stats.findIndex(s => s.chordType === chordType);
  const today = localDateKey();

  if (index >= 0) {
    stats[index].attempts += 1;
    if (correct) stats[index].correct += 1;
    stats[index].lastAttempted = today;
  } else {
    stats.push({
      chordType,
      attempts: 1,
      correct: correct ? 1 : 0,
      lastAttempted: today,
    });
  }

  setItem(STORAGE_KEYS.CHORD_STATS, stats);
  return stats;
}

// Scale Stats
export function getScaleStats(): ScaleStats[] {
  return getItem(STORAGE_KEYS.SCALE_STATS, []);
}

export function updateScaleStats(scaleType: string, correct: boolean): ScaleStats[] {
  const stats = getScaleStats();
  const index = stats.findIndex(s => s.scaleType === scaleType);
  const today = localDateKey();

  if (index >= 0) {
    stats[index].attempts += 1;
    if (correct) stats[index].correct += 1;
    stats[index].lastAttempted = today;
  } else {
    stats.push({
      scaleType,
      attempts: 1,
      correct: correct ? 1 : 0,
      lastAttempted: today,
    });
  }

  setItem(STORAGE_KEYS.SCALE_STATS, stats);
  return stats;
}

// Interval Stats
export function getIntervalStats(): IntervalStats[] {
  return getItem(STORAGE_KEYS.INTERVAL_STATS, []);
}

export function updateIntervalStats(intervalType: string, correct: boolean): IntervalStats[] {
  const stats = getIntervalStats();
  const index = stats.findIndex(s => s.intervalType === intervalType);
  const today = localDateKey();

  if (index >= 0) {
    stats[index].attempts += 1;
    if (correct) stats[index].correct += 1;
    stats[index].lastAttempted = today;
  } else {
    stats.push({
      intervalType,
      attempts: 1,
      correct: correct ? 1 : 0,
      lastAttempted: today,
    });
  }

  setItem(STORAGE_KEYS.INTERVAL_STATS, stats);
  return stats;
}

// Key Stats (Music Keys mode)
export function getKeyStats(): KeyStats[] {
  return getItem(STORAGE_KEYS.KEY_STATS, []);
}

export function updateKeyStats(keyType: string, correct: boolean): KeyStats[] {
  const stats = getKeyStats();
  const index = stats.findIndex(s => s.keyType === keyType);
  const today = localDateKey();

  if (index >= 0) {
    stats[index].attempts += 1;
    if (correct) stats[index].correct += 1;
    stats[index].lastAttempted = today;
  } else {
    stats.push({
      keyType,
      attempts: 1,
      correct: correct ? 1 : 0,
      lastAttempted: today,
    });
  }

  setItem(STORAGE_KEYS.KEY_STATS, stats);
  return stats;
}

// Music Keys Level Progress
export interface MusicKeysLevelProgress {
  levelId: number;
  questionsCompleted: number;
  questionsRequired: number;
  bestScore: number;
  timesCompleted: number;
  lastPlayedDate: string;
}

export function getMusicKeysProgress(): MusicKeysLevelProgress[] {
  return getItem(STORAGE_KEYS.MUSIC_KEYS_PROGRESS, []);
}

export function updateMusicKeysProgress(levelId: number, updates: Partial<MusicKeysLevelProgress>): MusicKeysLevelProgress[] {
  const progress = getMusicKeysProgress();
  const index = progress.findIndex(p => p.levelId === levelId);

  if (index >= 0) {
    progress[index] = { ...progress[index], ...updates };
  } else {
    progress.push({
      levelId,
      questionsCompleted: 0,
      questionsRequired: 15,
      bestScore: 0,
      timesCompleted: 0,
      lastPlayedDate: localDateKey(),
      ...updates,
    });
  }

  setItem(STORAGE_KEYS.MUSIC_KEYS_PROGRESS, progress);
  return progress;
}

// Note Stats (Notes mode)
export interface NoteStats {
  noteType: string; // e.g., "C4", "F#3"
  attempts: number;
  correct: number;
  lastAttempted: string;
}

export function getNoteStats(): NoteStats[] {
  return getItem(STORAGE_KEYS.NOTE_STATS, []);
}

export function updateNoteStats(noteType: string, correct: boolean): NoteStats[] {
  const stats = getNoteStats();
  const index = stats.findIndex(s => s.noteType === noteType);
  const today = localDateKey();

  if (index >= 0) {
    stats[index].attempts += 1;
    if (correct) stats[index].correct += 1;
    stats[index].lastAttempted = today;
  } else {
    stats.push({
      noteType,
      attempts: 1,
      correct: correct ? 1 : 0,
      lastAttempted: today,
    });
  }

  setItem(STORAGE_KEYS.NOTE_STATS, stats);
  return stats;
}

// Notes Level Progress
export interface NotesLevelProgress {
  levelId: number;
  questionsCompleted: number;
  questionsRequired: number;
  bestScore: number;
  timesCompleted: number;
  lastPlayedDate: string;
}

export function getNotesProgress(): NotesLevelProgress[] {
  return getItem(STORAGE_KEYS.NOTES_PROGRESS, []);
}

export function updateNotesProgress(levelId: number, updates: Partial<NotesLevelProgress>): NotesLevelProgress[] {
  const progress = getNotesProgress();
  const index = progress.findIndex(p => p.levelId === levelId);

  if (index >= 0) {
    progress[index] = { ...progress[index], ...updates };
  } else {
    progress.push({
      levelId,
      questionsCompleted: 0,
      questionsRequired: 15,
      bestScore: 0,
      timesCompleted: 0,
      lastPlayedDate: localDateKey(),
      ...updates,
    });
  }

  setItem(STORAGE_KEYS.NOTES_PROGRESS, progress);
  return progress;
}

// Helper: get the ISO date string for Monday of the current week
function getWeekStart(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now.getFullYear(), now.getMonth(), diff);
  return localDateKey(monday);
}

// Weekly Goals
export interface WeeklyGoal {
  id: string;
  type: 'questions' | 'minutes' | 'accuracy' | 'streak';
  target: number;
  current: number;
  weekStart: string; // ISO date string of Monday
  completed: boolean;
  accuracySessions?: number; // number of sessions averaged for 'accuracy' type
}

export interface WeeklyGoalsData {
  goals: WeeklyGoal[];
  weekStart: string;
  totalWeeksCompleted: number;
}

export function getWeeklyGoals(): WeeklyGoalsData {
  const defaultData: WeeklyGoalsData = {
    goals: [],
    weekStart: getWeekStart(),
    totalWeeksCompleted: 0,
  };
  const data = getItem(STORAGE_KEYS.WEEKLY_GOALS, defaultData);
  // Reset if new week
  if (data.weekStart !== getWeekStart()) {
    const wasCompleted = data.goals.length > 0 && data.goals.every(g => g.completed);
    return {
      // accuracySessions has to reset with `current`. Preserving it made the
      // accuracy goal average a fresh week's first score against last week's
      // session count: with 20 carried over, a 100%-accurate session computed
      // (0 * 20 + 100) / 21 ≈ 5, and the goal could never be completed again.
      goals: data.goals.map(g => ({ ...g, current: 0, completed: false, accuracySessions: 0 })),
      weekStart: getWeekStart(),
      totalWeeksCompleted: data.totalWeeksCompleted + (wasCompleted ? 1 : 0),
    };
  }
  return data;
}

export function setWeeklyGoals(goals: Omit<WeeklyGoal, 'id' | 'current' | 'completed' | 'weekStart'>[]): WeeklyGoalsData {
  const data = getWeeklyGoals();
  data.goals = goals.map((g, i) => ({
    ...g,
    id: `goal_${i}_${Date.now()}`,
    current: 0,
    completed: false,
    weekStart: getWeekStart(),
  }));
  data.weekStart = getWeekStart();
  setItem(STORAGE_KEYS.WEEKLY_GOALS, data);
  return data;
}

export function updateWeeklyGoalProgress(type: WeeklyGoal['type'], amount: number): WeeklyGoalsData {
  const data = getWeeklyGoals();
  data.goals = data.goals.map(goal => {
    if (goal.type === type) {
      let newCurrent: number;
      let updatedFields: Partial<WeeklyGoal> = {};
      if (type === 'accuracy') {
        // Average accuracy across all sessions this week
        const sessions = (goal.accuracySessions ?? 0) + 1;
        newCurrent = (goal.current * (sessions - 1) + amount) / sessions;
        updatedFields = { accuracySessions: sessions };
      } else if (type === 'streak') {
        // 'Best Streak' is a high-water mark, not a sum.
        newCurrent = Math.max(goal.current, amount);
      } else {
        newCurrent = goal.current + amount;
      }
      return { ...goal, ...updatedFields, current: newCurrent, completed: newCurrent >= goal.target };
    }
    return goal;
  });
  setItem(STORAGE_KEYS.WEEKLY_GOALS, data);
  return data;
}

// Streak Freeze
export interface StreakFreezeData {
  freezesAvailable: number;
  freezesUsedThisWeek: number;
  lastFreezeDate: string;
  weekStart: string;
  autoFreezeEnabled: boolean;
}

export function getStreakFreezeData(): StreakFreezeData {
  const defaultData: StreakFreezeData = {
    freezesAvailable: 1,
    freezesUsedThisWeek: 0,
    lastFreezeDate: '',
    weekStart: getWeekStart(),
    autoFreezeEnabled: true,
  };
  const data = getItem(STORAGE_KEYS.STREAK_FREEZE, defaultData);
  // Reset weekly allowance
  if (data.weekStart !== getWeekStart()) {
    return { ...data, freezesUsedThisWeek: 0, freezesAvailable: 1, weekStart: getWeekStart() };
  }
  return data;
}

export function useStreakFreeze(): boolean {
  const data = getStreakFreezeData();
  if (data.freezesAvailable <= 0 || data.freezesUsedThisWeek >= 1) return false;
  const today = localDateKey();
  if (data.lastFreezeDate === today) return false;
  const updated: StreakFreezeData = {
    ...data,
    freezesAvailable: data.freezesAvailable - 1,
    freezesUsedThisWeek: data.freezesUsedThisWeek + 1,
    lastFreezeDate: today,
  };
  setItem(STORAGE_KEYS.STREAK_FREEZE, updated);
  return true;
}

export function updateStreakFreezeSettings(autoFreeze: boolean): void {
  const data = getStreakFreezeData();
  setItem(STORAGE_KEYS.STREAK_FREEZE, { ...data, autoFreezeEnabled: autoFreeze });
}

// Social Challenges
export interface SocialChallenge {
  id: string;
  creatorName: string;
  mode: string;
  level: number;
  questionSeed: number;
  questionCount: number;
  creatorScore: number;
  creatorAccuracy: number;
  createdDate: string;
  completed: boolean;
  playerScore?: number;
  playerAccuracy?: number;
}

export function getSocialChallenges(): SocialChallenge[] {
  return getItem(STORAGE_KEYS.SOCIAL_CHALLENGES, []);
}

export function createSocialChallenge(challenge: Omit<SocialChallenge, 'id' | 'completed'>): SocialChallenge {
  const challenges = getSocialChallenges();
  const newChallenge: SocialChallenge = {
    ...challenge,
    id: `challenge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    completed: false,
  };
  challenges.push(newChallenge);
  setItem(STORAGE_KEYS.SOCIAL_CHALLENGES, challenges);
  return newChallenge;
}

export function completeSocialChallenge(id: string, score: number, accuracy: number): void {
  const challenges = getSocialChallenges();
  const index = challenges.findIndex(c => c.id === id);
  if (index >= 0) {
    challenges[index] = { ...challenges[index], completed: true, playerScore: score, playerAccuracy: accuracy };
    setItem(STORAGE_KEYS.SOCIAL_CHALLENGES, challenges);
  }
}

// Mastery Data
export interface MasteryItem {
  type: 'chord' | 'scale' | 'interval' | 'key' | 'note';
  value: string;
  attempts: number;
  correct: number;
  masteryLevel: number; // 0-100
  lastPracticed: string;
}

export function getMasteryData(): MasteryItem[] {
  return getItem(STORAGE_KEYS.MASTERY_DATA, []);
}

export function updateMasteryItem(type: MasteryItem['type'], value: string, isCorrect: boolean): MasteryItem {
  const data = getMasteryData();
  const today = localDateKey();
  let item = data.find(d => d.type === type && d.value === value);

  if (!item) {
    item = { type, value, attempts: 0, correct: 0, masteryLevel: 0, lastPracticed: today };
    data.push(item);
  }

  item.attempts++;
  if (isCorrect) item.correct++;
  item.lastPracticed = today;

  // Calculate mastery level (weighted recent accuracy with minimum attempts)
  const accuracy = item.attempts > 0 ? item.correct / item.attempts : 0;
  const confidence = Math.min(1, item.attempts / 20); // Need 20 attempts for full confidence
  item.masteryLevel = Math.round(accuracy * confidence * 100);

  setItem(STORAGE_KEYS.MASTERY_DATA, data);
  return item;
}

export function getMasteryByType(type: MasteryItem['type']): MasteryItem[] {
  return getMasteryData().filter(d => d.type === type);
}

// Daily Stats
export function getDailyStats(): DailyStats {
  return getItem(STORAGE_KEYS.DAILY_STATS, getDefaultDailyStats());
}

export function updateDailyStats(updates: Partial<DailyStats>): DailyStats {
  const current = getDailyStats();
  const updated = { ...current, ...updates };
  setItem(STORAGE_KEYS.DAILY_STATS, updated);
  return updated;
}

export function checkAndUpdateDailyStreak(): DailyStats {
  const stats = getDailyStats();
  const today = localDateKey();
  const yesterday = localDateKeyDaysAgo(1);

  if (stats.lastPlayedDate === today) {
    return stats; // Already played today
  }

  let newStreak = stats.currentStreak;
  if (stats.lastPlayedDate === yesterday) {
    newStreak += 1; // Continue streak
  } else if (stats.lastPlayedDate === localDateKeyDaysAgo(2)) {
    /*
     * Exactly one day was missed, which is what a streak freeze is for.
     *
     * This branch used to catch *any* gap that wasn't today or yesterday, so
     * a two-day gap and a two-year gap were treated identically: someone
     * returning after six months resumed their old streak +1, and since the
     * allowance regenerates weekly, a once-a-week player could accumulate a
     * "30 day streak" over 30 weeks. It also fired for a brand-new user,
     * whose lastPlayedDate is '', burning their first freeze for no benefit —
     * newStreak is 1 either way.
     */
    const freezeData = getStreakFreezeData();
    if (freezeData.autoFreezeEnabled && useStreakFreeze()) {
      newStreak += 1; // Streak preserved by freeze
    } else {
      newStreak = 1;
    }
  } else {
    newStreak = 1; // Start new streak
  }

  return updateDailyStats({
    lastPlayedDate: today,
    currentStreak: newStreak,
    longestStreak: Math.max(stats.longestStreak, newStreak),
    totalDaysPlayed: stats.totalDaysPlayed + 1,
    completed: false,
    todayScore: 0,
    todayQuestions: 0,
  });
}

// Game Mode Stats
export function getGameModeStats(): GameModeStats[] {
  return getItem(STORAGE_KEYS.GAME_MODE_STATS, []);
}

export function updateGameModeStats(mode: string, score: number, time?: number): GameModeStats[] {
  const stats = getGameModeStats();
  const index = stats.findIndex(s => s.mode === mode);
  const today = localDateKey();

  if (index >= 0) {
    const existing = stats[index];
    stats[index] = {
      ...existing,
      timesPlayed: existing.timesPlayed + 1,
      bestScore: Math.max(existing.bestScore, score),
      totalScore: existing.totalScore + score,
      averageScore: Math.round((existing.totalScore + score) / (existing.timesPlayed + 1)),
      bestTime: time ? Math.min(existing.bestTime || Infinity, time) : existing.bestTime,
      lastPlayedDate: today,
    };
  } else {
    stats.push({
      mode,
      timesPlayed: 1,
      bestScore: score,
      totalScore: score,
      averageScore: score,
      bestTime: time || 0,
      lastPlayedDate: today,
    });
  }

  setItem(STORAGE_KEYS.GAME_MODE_STATS, stats);
  return stats;
}

// Session History
const MAX_SESSION_HISTORY = 50; // Keep last 50 sessions

export function getSessionHistory(): PracticeSession[] {
  return getItem(STORAGE_KEYS.SESSION_HISTORY, []);
}

export function addSessionToHistory(session: Omit<PracticeSession, 'id'>): PracticeSession[] {
  const history = getSessionHistory();
  const newSession: PracticeSession = {
    ...session,
    id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  };

  // Add to beginning, limit to max
  const updated = [newSession, ...history].slice(0, MAX_SESSION_HISTORY);
  setItem(STORAGE_KEYS.SESSION_HISTORY, updated);
  return updated;
}

// Practice Insights derived from session history
export interface PracticeInsights {
  totalSessions: number;
  totalPracticeTime: number; // in seconds
  averageAccuracy: number;
  bestStreak: number;
  weeklyProgress: {
    date: string;
    sessions: number;
    accuracy: number;
    xp: number;
  }[];
  recentTrend: 'improving' | 'steady' | 'declining';
  strongestMode: string | null;
  weakestMode: string | null;
  practiceConsistency: number; // days practiced in last 7 days
}

export function getPracticeInsights(): PracticeInsights {
  const history = getSessionHistory();
  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  // Calculate total stats
  const totalSessions = history.length;
  const totalPracticeTime = history.reduce((sum, s) => sum + s.duration, 0);
  const averageAccuracy = totalSessions > 0
    ? history.reduce((sum, s) => sum + s.accuracy, 0) / totalSessions
    : 0;
  const bestStreak = Math.max(0, ...history.map(s => s.streak));

  // Calculate weekly progress (last 7 days)
  const weeklyProgress: PracticeInsights['weeklyProgress'] = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dateStr = localDateKey(date);
    const daySessions = history.filter(s => s.date === dateStr);
    weeklyProgress.push({
      date: dateStr,
      sessions: daySessions.length,
      accuracy: daySessions.length > 0
        ? daySessions.reduce((sum, s) => sum + s.accuracy, 0) / daySessions.length
        : 0,
      xp: daySessions.reduce((sum, s) => sum + s.xpEarned, 0),
    });
  }

  // Calculate trend (compare first half vs second half of recent sessions)
  let recentTrend: PracticeInsights['recentTrend'] = 'steady';
  if (history.length >= 6) {
    const recent = history.slice(0, 3);
    const older = history.slice(3, 6);
    const recentAvg = recent.reduce((sum, s) => sum + s.accuracy, 0) / 3;
    const olderAvg = older.reduce((sum, s) => sum + s.accuracy, 0) / 3;
    if (recentAvg > olderAvg + 5) recentTrend = 'improving';
    else if (recentAvg < olderAvg - 5) recentTrend = 'declining';
  }

  // Find strongest and weakest modes
  const modeStats = new Map<string, { total: number; correct: number }>();
  history.forEach(s => {
    const current = modeStats.get(s.mode) || { total: 0, correct: 0 };
    current.total += s.totalQuestions;
    current.correct += s.correctAnswers;
    modeStats.set(s.mode, current);
  });

  let strongestMode: string | null = null;
  let weakestMode: string | null = null;
  let highestAccuracy = 0;
  let lowestAccuracy = 100;

  modeStats.forEach((stats, mode) => {
    if (stats.total >= 5) { // Minimum 5 questions to count
      const acc = (stats.correct / stats.total) * 100;
      if (acc > highestAccuracy) {
        highestAccuracy = acc;
        strongestMode = mode;
      }
      if (acc < lowestAccuracy) {
        lowestAccuracy = acc;
        weakestMode = mode;
      }
    }
  });

  // Calculate practice consistency (unique days in last 7)
  const recentSessions = history.filter(s => new Date(s.date) >= oneWeekAgo);
  const uniqueDays = new Set(recentSessions.map(s => s.date)).size;

  return {
    totalSessions,
    totalPracticeTime,
    averageAccuracy: Math.round(averageAccuracy * 10) / 10,
    bestStreak,
    weeklyProgress,
    recentTrend,
    strongestMode,
    weakestMode,
    practiceConsistency: uniqueDays,
  };
}

// Achievements
interface StoredAchievement {
  id: string;
  unlockedDate: string;
}

export function getUnlockedAchievements(): Achievement[] {
  // Try to get achievements with dates first (new format)
  const unlockedWithDates = getItem<StoredAchievement[]>('keyperfect_achievements_with_dates', []);
  const unlockedIds = getItem<string[]>(STORAGE_KEYS.ACHIEVEMENTS, []);

  return ACHIEVEMENTS.filter(a => unlockedIds.includes(a.id)).map(a => {
    const storedData = unlockedWithDates.find(s => s.id === a.id);
    return {
      ...a,
      unlockedDate: storedData?.unlockedDate,
    };
  });
}

export function checkAndUnlockAchievements(stats: UserStats): Achievement[] {
  const unlocked = getItem<string[]>(STORAGE_KEYS.ACHIEVEMENTS, []);
  const unlockedWithDates = getItem<StoredAchievement[]>('keyperfect_achievements_with_dates', []);
  const newlyUnlocked: Achievement[] = [];
  const today = localDateKey();

  ACHIEVEMENTS.forEach(achievement => {
    if (unlocked.includes(achievement.id)) return;

    let shouldUnlock = false;

    switch (achievement.category) {
      case 'progress':
        if (achievement.id === 'first_correct' && stats.totalCorrect >= 1) shouldUnlock = true;
        if (achievement.id === 'questions_100' && stats.totalQuestionsAnswered >= 100) shouldUnlock = true;
        if (achievement.id === 'questions_500' && stats.totalQuestionsAnswered >= 500) shouldUnlock = true;
        if (achievement.id === 'questions_1000' && stats.totalQuestionsAnswered >= 1000) shouldUnlock = true;
        if (achievement.id === 'questions_5000' && stats.totalQuestionsAnswered >= 5000) shouldUnlock = true;
        break;

      case 'streak': {
        // In-session answer streaks
        if (achievement.id === 'streak_5' && stats.longestStreak >= 5) shouldUnlock = true;
        if (achievement.id === 'streak_10' && stats.longestStreak >= 10) shouldUnlock = true;
        if (achievement.id === 'streak_25' && stats.longestStreak >= 25) shouldUnlock = true;
        if (achievement.id === 'streak_50' && stats.longestStreak >= 50) shouldUnlock = true;
        // Daily login streaks
        const dailyStats = getDailyStats();
        if (achievement.id === 'daily_3' && dailyStats.currentStreak >= 3) shouldUnlock = true;
        if (achievement.id === 'daily_7' && dailyStats.currentStreak >= 7) shouldUnlock = true;
        if (achievement.id === 'daily_30' && dailyStats.currentStreak >= 30) shouldUnlock = true;
        break;
      }

      case 'accuracy':
        const accuracy = stats.totalQuestionsAnswered > 0
          ? (stats.totalCorrect / stats.totalQuestionsAnswered) * 100
          : 0;
        if (achievement.id === 'accuracy_80' && accuracy >= 80 && stats.totalQuestionsAnswered >= 50) shouldUnlock = true;
        if (achievement.id === 'accuracy_90' && accuracy >= 90 && stats.totalQuestionsAnswered >= 100) shouldUnlock = true;
        if (achievement.id === 'accuracy_95' && accuracy >= 95 && stats.totalQuestionsAnswered >= 200) shouldUnlock = true;
        break;

      case 'mastery': {
        const levelProgress = getLevelProgress();
        if (achievement.id === 'level_complete_1' && levelProgress.some(p => p.levelId === 1 && p.timesCompleted > 0)) shouldUnlock = true;
        if (achievement.id === 'level_complete_4' && levelProgress.some(p => p.levelId === 4 && p.timesCompleted > 0)) shouldUnlock = true;
        if (achievement.id === 'level_complete_8' && levelProgress.some(p => p.levelId === 8 && p.timesCompleted > 0)) shouldUnlock = true;
        break;
      }

      case 'challenge': {
        const modeStats = getGameModeStats();
        const speedrunStats = modeStats.find(s => s.mode === 'speedrun');
        const survivalStats = modeStats.find(s => s.mode === 'survival');
        if (achievement.id === 'speed_run_perfect' && speedrunStats && speedrunStats.bestScore >= 100) shouldUnlock = true;
        if (achievement.id === 'survival_100' && survivalStats && survivalStats.bestScore >= 100) shouldUnlock = true;
        break;
      }

      case 'special':
        const hour = new Date().getHours();
        if (achievement.id === 'night_owl' && hour >= 0 && hour < 5) shouldUnlock = true;
        if (achievement.id === 'early_bird' && hour >= 5 && hour < 8) shouldUnlock = true;
        if (achievement.id === 'all_instruments') {
          const usedInstruments = getUsedInstruments();
          if (usedInstruments.length >= achievement.requirement) shouldUnlock = true;
        }
        break;
    }

    if (shouldUnlock) {
      unlocked.push(achievement.id);
      unlockedWithDates.push({ id: achievement.id, unlockedDate: today });
      newlyUnlocked.push({ ...achievement, unlockedDate: today });
    }
  });

  if (newlyUnlocked.length > 0) {
    setItem(STORAGE_KEYS.ACHIEVEMENTS, unlocked);
    setItem('keyperfect_achievements_with_dates', unlockedWithDates);
  }

  return newlyUnlocked;
}

// Settings
export function getSettings(): AppSettings {
  return getItem(STORAGE_KEYS.SETTINGS, getDefaultSettings());
}

export function updateSettings(updates: Partial<AppSettings>): AppSettings {
  const current = getSettings();
  const updated = { ...current, ...updates };
  setItem(STORAGE_KEYS.SETTINGS, updated);
  return updated;
}

// Instrument usage tracking
export function getUsedInstruments(): InstrumentType[] {
  return getItem<InstrumentType[]>(STORAGE_KEYS.USED_INSTRUMENTS, []);
}

export function trackInstrumentUsage(instrument: InstrumentType): InstrumentType[] {
  const used = getUsedInstruments();
  if (!used.includes(instrument)) {
    used.push(instrument);
    setItem(STORAGE_KEYS.USED_INSTRUMENTS, used);
  }
  return used;
}

// Reset all data. STORAGE_KEYS misses several stores written elsewhere
// (keyperfect_srs, keyperfect_accessibility, keyperfect_tutorial_completed,
// keyperfect_leaderboard, keyperfect_achievements_with_dates, the cloud-sync
// auth/token/pending-sync keys, keyperfect_custom_sets, ...). Iterate the
// whole localStorage keyspace so 'Reset All Data' really wipes everything
// the app owns.
export function resetAllData(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('keyperfect_')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

// Export data
/**
 * Bumped when the shape of an exported field changes in a way an older import
 * cannot read. Files written before versioning began carry no `schemaVersion`
 * and are treated as version 1.
 */
export const EXPORT_SCHEMA_VERSION = 2;

export const EXPORT_APP = 'keyperfect';

export function exportData(): string {
  const data = {
    app: EXPORT_APP,
    schemaVersion: EXPORT_SCHEMA_VERSION,
    appVersion: APP_VERSION,
    userStats: getUserStats(),
    levelProgress: getLevelProgress(),
    chordStats: getChordStats(),
    scaleStats: getScaleStats(),
    intervalStats: getIntervalStats(),
    keyStats: getKeyStats(),
    noteStats: getNoteStats(),
    musicKeysProgress: getMusicKeysProgress(),
    notesProgress: getNotesProgress(),
    dailyStats: getDailyStats(),
    gameModeStats: getGameModeStats(),
    achievements: getItem<string[]>(STORAGE_KEYS.ACHIEVEMENTS, []),
    settings: getSettings(),
    sessionHistory: getSessionHistory(),
    usedInstruments: getUsedInstruments(),
    weeklyGoals: getWeeklyGoals(),
    streakFreeze: getStreakFreezeData(),
    socialChallenges: getSocialChallenges(),
    masteryData: getMasteryData(),
    exportDate: new Date().toISOString(),
  };
  return JSON.stringify(data, null, 2);
}

// Import data
export type ImportErrorCode =
  | 'empty'
  | 'not_json'
  | 'not_an_object'
  | 'foreign_file'
  | 'no_recognised_data'
  | 'too_new'
  | 'write_failed';

export interface ImportResult {
  ok: boolean;
  code?: ImportErrorCode;
  /** One sentence, safe to show verbatim. */
  message: string;
  /** Fields actually restored, for the success summary. */
  restored?: string[];
  schemaVersion?: number;
}

/** Every field importData knows how to restore, in the order it writes them. */
const IMPORTABLE_FIELDS: [field: string, key: string][] = [
  ['userStats', STORAGE_KEYS.USER_STATS],
  ['levelProgress', STORAGE_KEYS.LEVEL_PROGRESS],
  ['chordStats', STORAGE_KEYS.CHORD_STATS],
  ['scaleStats', STORAGE_KEYS.SCALE_STATS],
  ['intervalStats', STORAGE_KEYS.INTERVAL_STATS],
  ['keyStats', STORAGE_KEYS.KEY_STATS],
  ['noteStats', STORAGE_KEYS.NOTE_STATS],
  ['musicKeysProgress', STORAGE_KEYS.MUSIC_KEYS_PROGRESS],
  ['notesProgress', STORAGE_KEYS.NOTES_PROGRESS],
  ['dailyStats', STORAGE_KEYS.DAILY_STATS],
  ['gameModeStats', STORAGE_KEYS.GAME_MODE_STATS],
  ['achievements', STORAGE_KEYS.ACHIEVEMENTS],
  ['settings', STORAGE_KEYS.SETTINGS],
  ['sessionHistory', STORAGE_KEYS.SESSION_HISTORY],
  ['usedInstruments', STORAGE_KEYS.USED_INSTRUMENTS],
  ['weeklyGoals', STORAGE_KEYS.WEEKLY_GOALS],
  ['streakFreeze', STORAGE_KEYS.STREAK_FREEZE],
  ['socialChallenges', STORAGE_KEYS.SOCIAL_CHALLENGES],
  ['masteryData', STORAGE_KEYS.MASTERY_DATA],
];

/**
 * Validates before writing anything.
 *
 * The previous version returned `true` for any input that merely parsed as
 * JSON — `{}`, `[]`, `"hello"`, or somebody else's backup — so an import that
 * restored nothing at all reported success.
 */
export function importDataDetailed(jsonString: string): ImportResult {
  if (typeof jsonString !== 'string' || jsonString.trim() === '') {
    return { ok: false, code: 'empty', message: 'That file is empty. Pick a KeyPerfect backup file and try again.' };
  }

  let data: unknown;
  try {
    data = JSON.parse(jsonString);
  } catch {
    return {
      ok: false,
      code: 'not_json',
      message: "That file isn't valid JSON. Export a fresh backup from Settings and import that.",
    };
  }

  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return {
      ok: false,
      code: 'not_an_object',
      message: "That file isn't a KeyPerfect backup. Look for a file named keyperfect-backup-<date>.json.",
    };
  }

  const record = data as Record<string, unknown>;

  if (typeof record.app === 'string' && record.app !== EXPORT_APP) {
    return {
      ok: false,
      code: 'foreign_file',
      message: `That backup was written by "${record.app}", not KeyPerfect.`,
    };
  }

  const rawVersion = record.schemaVersion;
  // Pre-versioning exports are still readable: the fields they carry are a
  // subset of today's, so they import as version 1.
  const schemaVersion = typeof rawVersion === 'number' && Number.isFinite(rawVersion) ? rawVersion : 1;

  if (schemaVersion > EXPORT_SCHEMA_VERSION) {
    return {
      ok: false,
      code: 'too_new',
      schemaVersion,
      message: `That backup was written by a newer version of KeyPerfect (format ${schemaVersion}, this build reads ${EXPORT_SCHEMA_VERSION}). Update the app, then import again.`,
    };
  }

  const present = IMPORTABLE_FIELDS.filter(([field]) => {
    const value = record[field];
    return value !== undefined && value !== null;
  });

  if (present.length === 0) {
    return {
      ok: false,
      code: 'no_recognised_data',
      schemaVersion,
      message: 'That file has no KeyPerfect progress in it. Nothing was changed.',
    };
  }

  try {
    for (const [field, key] of present) {
      setItem(key, record[field]);
    }
  } catch (error) {
    console.error('Import failed while writing:', error);
    return {
      ok: false,
      code: 'write_failed',
      schemaVersion,
      message: "Couldn't save the imported data — your browser storage may be full or blocked.",
    };
  }

  return {
    ok: true,
    schemaVersion,
    restored: present.map(([field]) => field),
    message: `Restored ${present.length} section${present.length === 1 ? '' : 's'} from your backup.`,
  };
}

/** Boolean wrapper kept for callers that only need pass/fail. */
export function importData(jsonString: string): boolean {
  return importDataDetailed(jsonString).ok;
}
